import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useOnboardingChecklist } from '../../hooks/useApi';
import { ChecklistSection } from '../../types/api.types';
import styles from './OnboardingProgress.module.scss';

const CATEGORY_ICONS: Record<string, string> = {
    timeline: '🗺',
    income: '💼',
    assets: '🏦',
    expenses: '📊',
};

function SectionRow({ section, onSkip }: { section: ChecklistSection; onSkip: (id: string) => void }) {
    const navigate = useNavigate();

    return (
        <div className={`${styles.sectionRow} ${styles[section.status]}`}>
            <div className={styles.sectionLeft}>
                <span className={styles.sectionIcon}>{CATEGORY_ICONS[section.category] ?? '○'}</span>
                <div className={styles.sectionInfo}>
                    <span className={styles.sectionLabel}>{section.label}</span>
                    <span className={styles.sectionDesc}>{section.description}</span>
                </div>
            </div>
            <div className={styles.sectionActions}>
                {section.status === 'complete' ? (
                    <span className={styles.completeBadge}>Done</span>
                ) : section.status === 'skipped' ? (
                    <button className={styles.unskipButton} onClick={() => onSkip(section.id)}>
                        Undo skip
                    </button>
                ) : (
                    <>
                        <button className={styles.fillButton} onClick={() => navigate(section.route)}>
                            Set up
                        </button>
                        <button className={styles.skipButton} onClick={() => onSkip(section.id)}>
                            Skip
                        </button>
                    </>
                )}
            </div>
        </div>
    );
}

export function OnboardingProgress() {
    const { checklist, loading, skipSection } = useOnboardingChecklist();
    const [collapsed, setCollapsed] = useState(false);

    if (loading || !checklist) return null;
    if (checklist.totalCount === 0) return null;
    if (checklist.completedCount >= checklist.totalCount) return null;

    const progressPct = checklist.totalCount > 0 ? (checklist.completedCount / checklist.totalCount) * 100 : 0;

    return (
        <div className={styles.widget}>
            <div className={styles.header}>
                <div className={styles.headerLeft}>
                    <span className={styles.title}>Setup Progress</span>
                    <span className={styles.subtitle}>
                        {checklist.completedCount} of {checklist.totalCount} sections complete
                    </span>
                </div>
                <button
                    className={styles.collapseButton}
                    onClick={() => setCollapsed((c) => !c)}
                    title={collapsed ? 'Expand' : 'Collapse'}
                >
                    {collapsed ? '▼' : '▲'}
                </button>
            </div>

            <div className={styles.progressBar}>
                <div className={styles.progressFill} style={{ width: `${progressPct}%` }} />
            </div>

            {!collapsed && (
                <div className={styles.sectionList}>
                    {checklist.sections.map((section) => (
                        <SectionRow key={section.id} section={section} onSkip={skipSection} />
                    ))}
                </div>
            )}
        </div>
    );
}
